
import React, { useState } from 'react';
// Corrected import path to point to the root types.ts file to ensure full Director type availability
import { Language, Director, ViewState } from '../types';
import { translations } from '../utils/translations';
import { filmmakersContent } from '../data/filmmakersContent';

// Extendemos localmente el tipo Director para incluir el hover si existe
interface DirectorExtended extends Director {
  id: string;
  name: string;
  role: string;
  desc: string;
  img: string;
  imgHover?: string;
}

interface DirectorsProps {
  language: Language;
  onSelect: (director: Director) => void;
  setView?: (view: ViewState) => void;
}

export const Directors: React.FC<DirectorsProps> = ({ language, onSelect, setView }) => {
  const langTranslations = (translations as any)[language].directors;
  const heroTranslations = (translations as any)[language].hero;

  // Transform centralized data into the format expected by the UI (localized)
  const team: DirectorExtended[] = filmmakersContent.map(filmmaker => ({
    id: filmmaker.id,
    name: filmmaker.name,
    role: filmmaker.roles[language],
    desc: filmmaker.bios[language],
    img: filmmaker.img,
    imgHover: filmmaker.imgHover
  }));

  // URL del Demo Reel oficial de ¡RR3VERENT!A
  const reelYoutubeUrl = "https://www.youtube.com/embed/YxuKUKFTWPM?si=7E9u2Rj_5iFSf5NW&autoplay=1&rel=0";

  return (
    <div className="pt-24 pb-20 min-h-screen bg-[#ccff00] text-black overflow-x-hidden scanlines relative">
      <div className="max-w-[1600px] mx-auto px-6">
        
        {/* Header Section */}
        <div className="flex flex-col items-center mb-20 gap-8">
          {/* Header Box with 8-bit shadow */}
          <div className="bg-black text-white px-10 py-4 inline-block transform -rotate-2 border-4 border-white shadow-[12px_12px_0px_0px_#b026ff]">
            <h2 className="font-display font-black text-6xl md:text-8xl uppercase tracking-tighter leading-none">
              {langTranslations.title}
            </h2>
          </div>

          {/* DEMO REEL Button below Title */}
          <button 
            onClick={() => setView?.(ViewState.DEMO_REEL)}
            className="btn-8bit !bg-black !text-acid border-2 border-acid group flex items-center gap-3 text-lg md:text-xl px-8 md:px-12 py-3 md:py-4 hover:!bg-acid hover:!text-black transition-all shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          >
            {heroTranslations.demoReel}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="currentColor" 
              viewBox="0 0 24 24" 
              className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-125 transition-transform"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-[1500px] mx-auto">
          {team.map((member) => (
            <div 
              key={member.id} 
              className="bg-white p-5 border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[16px_16px_0px_0px_#b026ff] transition-all duration-150 group flex flex-col h-full relative"
            >
              {/* Image Frame */}
              <div className="border-4 border-black mb-5 relative overflow-hidden aspect-[4/5] bg-zinc-200">
                
                {/* Lógica de Imagen: Con efecto Glitch y 8-bit Loop en Hover */}
                {member.imgHover ? (
                  <>
                    <img 
                      src={member.img} 
                      alt={member.name} 
                      className="w-full h-full object-cover filter grayscale contrast-125 transition-opacity duration-150 group-hover:opacity-0"
                    />
                    <img 
                      src={member.imgHover} 
                      alt={`${member.name} color`} 
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 group-hover:animate-glitch-shot transition-opacity duration-150"
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none overflow-hidden">
                       <img 
                        src={member.imgHover} 
                        alt="" 
                        className="w-full h-full object-cover animate-glitch-8bit-loop"
                      />
                    </div>
                  </>
                ) : (
                  <img 
                    src={member.img} 
                    alt={member.name} 
                    className="w-full h-full object-cover filter grayscale contrast-125 group-hover:grayscale-0 transition-all duration-500"
                  />
                )}
                
                {/* Selected Works Button - Solo aparece en Hover */}
                <div className="absolute bottom-4 left-4 right-4 transition-all duration-500 transform translate-y-4 opacity-0 pointer-events-none group-hover:translate-y-0 group-hover:opacity-100 group-hover:pointer-events-auto z-20">
                   <button 
                     onClick={(e) => {
                       e.stopPropagation();
                       onSelect(member);
                     }}
                     className="w-full bg-black/80 backdrop-blur-sm text-[#ccff00] font-display font-black text-sm py-4 uppercase border-2 border-white shadow-[4px_4px_0px_0px_#ccff00] hover:bg-[#ccff00] hover:text-black hover:shadow-[6px_6px_0px_0px_#b026ff] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2"
                   >
                       {langTranslations.selectedWorks}
                       <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-4 h-4">
                         <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                       </svg>
                   </button>
                </div>
              </div>

              {/* Text Content */}
              <div className="flex flex-col flex-grow">
                 <h3 className="font-display font-black text-3xl uppercase mb-3 leading-none">{member.name}</h3>
                 
                 <div className="bg-black text-[#ccff00] inline-block px-3 py-1 mb-5 self-start border-r-4 border-b-4 border-[#b026ff]">
                    <span className="font-mono font-bold text-xs uppercase tracking-tighter">{member.role}</span>
                 </div>
                 
                 <p className="font-mono text-sm leading-tight border-t-4 border-black pt-4 mb-2">
                    {member.desc}
                 </p>
              </div>
              
              {/* Decorative Pixel bits */}
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-black"></div>
              <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-[#b026ff]"></div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};
